"use client";

/**
 * The "this vs that" studio.
 *
 * Collects the comparison, runs one Gemini Live session for narration and
 * transcript, then draws and encodes the video in this browser. There is no
 * editor handoff and nothing leaves the machine except the narration request.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Link from "next/link";
import { AlertCircle, ArrowRight, Loader2, Sparkles } from "lucide-react";
import { CHARACTERS, DEFAULT_CHARACTER_ID } from "@/comparison/characters";
import { DURATIONS, WATERMARK_CORNERS } from "@/comparison/form-options";
import {
	pickedImageFromFile,
	pickedImageFromSearchResult,
	releasePickedImage,
	type ImageSearchResult,
} from "@/comparison/image-picker";
import { ASPECTS } from "@/comparison/layout";
import {
	checkLicense,
	mintAgentToken,
	readStoredLicense,
	storeLicense,
	type LicenseStatus,
} from "@/comparison/license";
import { generateNarration } from "@/comparison/live-session";
import {
	readPreferences,
	readWatermarkImage,
	storePreferences,
	storeWatermarkImage,
} from "@/comparison/preferences";
import { buildScene, type Scene } from "@/comparison/scene";
import {
	DEFAULT_THEME_COLOR,
	recolouredSvgUrl,
	THEME_SWATCHES,
} from "@/comparison/theme";
import type {
	ComparisonSpec,
	GenerationProgress,
	NarrationResult,
	PickedImage,
	PoseKind,
	WatermarkCorner,
} from "@/comparison/types";
import {
	DEFAULT_LANGUAGE,
	DEFAULT_VOICE,
	LANGUAGES,
	VOICES,
} from "@/comparison/voices";
import { ImageSearchSheet } from "./image-search-sheet";
import { ImageSlot } from "./image-slot";
import { ResultView } from "./result-view";
import { SceneCanvas } from "./scene-canvas";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/utils/ui";

type ImageSlotKey = "thisImage" | "thatImage" | "background" | "watermarkImage";

const BACKGROUND_SWATCHES = [
	"#ffffff",
	"#f4f4f5",
	"#0f172a",
	"#4d2ef5",
	"#fef3c7",
];

const INITIAL_SPEC: ComparisonSpec = {
	topic: "",
	thisLabel: "",
	thatLabel: "",
	thisImage: null,
	thatImage: null,
	characterId: DEFAULT_CHARACTER_ID,
	themeColor: DEFAULT_THEME_COLOR,
	background: null,
	backgroundColor: "#ffffff",
	aspect: "9:16",
	languageLabel: DEFAULT_LANGUAGE,
	voiceName: DEFAULT_VOICE,
	targetSeconds: 30,
	showCaptions: true,
	watermarkText: "",
	watermarkImage: null,
	watermarkCorner: "bottom-right",
};

const SLOT_TITLES: Record<ImageSlotKey, string> = {
	thisImage: "Pick the left image",
	thatImage: "Pick the right image",
	background: "Pick a background",
	watermarkImage: "Pick a watermark",
};

interface Result {
	scene: Scene;
	narration: NarrationResult;
	spec: ComparisonSpec;
}

export function ComparisonStudio() {
	const [spec, setSpec] = useState<ComparisonSpec>(INITIAL_SPEC);
	const [license, setLicense] = useState("");
	const [licenseStatus, setLicenseStatus] = useState<LicenseStatus | null>(null);
	const [licenseError, setLicenseError] = useState<string | null>(null);
	const [checkingLicense, setCheckingLicense] = useState(false);
	const [progress, setProgress] = useState<GenerationProgress>({
		phase: "idle",
		message: "",
	});
	const [creditSpent, setCreditSpent] = useState(false);
	const [previewPose, setPreviewPose] = useState<PoseKind>("this");
	const [previewScene, setPreviewScene] = useState<Scene | null>(null);
	const [searchSlot, setSearchSlot] = useState<ImageSlotKey | null>(null);
	// Per-slot, not folded into `progress`: an image that won't load says nothing
	// about the generation, and routing it through the status panel put the
	// message on the far side of the page from the tile that caused it.
	const [busySlot, setBusySlot] = useState<ImageSlotKey | null>(null);
	const [slotError, setSlotError] = useState<{
		slot: ImageSlotKey;
		message: string;
	} | null>(null);
	const [result, setResult] = useState<Result | null>(null);
	/** Gates the settings write until the previous visit's settings are in. */
	const [restored, setRestored] = useState(false);
	/** The watermark already on disk, so restoring one doesn't re-encode it. */
	const watermarkRef = useRef<PickedImage | null>(null);

	const busy =
		progress.phase !== "idle" &&
		progress.phase !== "error" &&
		progress.phase !== "ready";

	const update = useCallback((patch: Partial<ComparisonSpec>) => {
		setSpec((previous) => ({ ...previous, ...patch }));
	}, []);

	const setSlot = useCallback((slot: ImageSlotKey, image: PickedImage | null) => {
		setSpec((previous) => {
			releasePickedImage({ image: previous[slot] });
			return { ...previous, [slot]: image };
		});
	}, []);

	// The chooser tiles are raw SVGs rather than canvas draws, so they'd keep
	// showing the authored blue while everything else follows the theme.
	const [poseThumbnails, setPoseThumbnails] = useState<Record<string, string>>(
		{},
	);
	useEffect(() => {
		let cancelled = false;
		void Promise.all(
			CHARACTERS.map(
				async (character) =>
					[
						character.id,
						await recolouredSvgUrl({
							url: character.poses.this,
							color: spec.themeColor,
						}),
					] as const,
			),
		)
			.then((entries) => {
				if (!cancelled) setPoseThumbnails(Object.fromEntries(entries));
			})
			.catch(() => {
				/* the untinted art is a fine fallback */
			});

		return () => {
			cancelled = true;
		};
	}, [spec.themeColor]);

	// Rebuild the preview scene from the same builder the export uses, so the
	// setup screen can't promise something the encoder won't produce.
	useEffect(() => {
		if (result) return;
		let cancelled = false;
		const timer = setTimeout(() => {
			void buildScene({ spec })
				.then((scene) => {
					if (!cancelled) setPreviewScene(scene);
				})
				.catch(() => {
					/* a missing image just leaves the last good preview up */
				});
		}, 120);

		return () => {
			cancelled = true;
			clearTimeout(timer);
		};
	}, [spec, result]);

	const verifyLicense = useCallback(async ({ key }: { key: string }) => {
		const trimmed = key.trim();
		if (!trimmed) return;
		setCheckingLicense(true);
		setLicenseError(null);
		try {
			const status = await checkLicense({ license: trimmed });
			setLicenseStatus(status);
			if (status.valid) storeLicense({ license: trimmed });
			else setLicenseError("That key wasn't recognised.");
		} catch (error) {
			setLicenseError(
				error instanceof Error ? error.message : "Couldn't check that key.",
			);
		} finally {
			setCheckingLicense(false);
		}
	}, []);

	// Restore the key from a previous visit so it only has to be pasted once.
	useEffect(() => {
		const stored = readStoredLicense();
		if (!stored) return;
		setLicense(stored);
		void verifyLicense({ key: stored });
	}, [verifyLicense]);

	// Settings from a previous visit. Restored in an effect rather than in the
	// initial state: this component is server-rendered, so reading localStorage
	// during the first render would hand React different markup than it hydrates
	// against. `readPreferences` falls back to `INITIAL_SPEC` field by field, so
	// a first-time visitor still gets exactly the defaults.
	useEffect(() => {
		setSpec((previous) => ({
			...previous,
			...readPreferences({ defaults: INITIAL_SPEC }),
		}));
		setRestored(true);

		let cancelled = false;
		void readWatermarkImage().then((image) => {
			if (!image) return;
			if (cancelled) {
				releasePickedImage({ image });
				return;
			}
			watermarkRef.current = image;
			setSlot("watermarkImage", image);
		});

		return () => {
			cancelled = true;
		};
	}, [setSlot]);

	// `restored` is state, not a ref, so this effect's first run already sees the
	// restored spec — a ref would let it fire once with the defaults still in
	// place and write those over what was stored.
	useEffect(() => {
		if (!restored) return;
		// Debounced because the colour picker updates the spec on every frame of
		// a drag.
		const timer = setTimeout(() => storePreferences({ spec }), 400);
		return () => clearTimeout(timer);
	}, [spec, restored]);

	// Kept out of the settings write: this one is a file, it costs a read to
	// encode, and it only changes when a watermark is actually picked.
	useEffect(() => {
		if (!restored) return;
		if (spec.watermarkImage === watermarkRef.current) return;
		watermarkRef.current = spec.watermarkImage;
		void storeWatermarkImage({ image: spec.watermarkImage });
	}, [spec.watermarkImage, restored]);

	const handleFile = useCallback(
		async ({ slot, file }: { slot: ImageSlotKey; file: File }) => {
			setBusySlot(slot);
			setSlotError(null);
			try {
				setSlot(slot, await pickedImageFromFile({ file }));
			} catch (error) {
				setSlotError({
					slot,
					message:
						error instanceof Error ? error.message : "Couldn't read that image.",
				});
			} finally {
				setBusySlot(null);
			}
		},
		[setSlot],
	);

	// Throws on failure so the sheet can keep itself open and show why; it closes
	// only once the bytes are actually in the form.
	const handleSearchPick = useCallback(
		async ({ result: picked }: { result: ImageSearchResult }) => {
			const slot = searchSlot;
			if (!slot) return;

			const image = await pickedImageFromSearchResult({ result: picked });
			setSlot(slot, image);
			setSlotError(null);
			// A logo search usually names the thing being compared, and the label
			// is what the narrator has to say for the pointing to line up.
			if (slot === "thisImage" && !spec.thisLabel.trim()) {
				update({ thisLabel: picked.title });
			}
			if (slot === "thatImage" && !spec.thatLabel.trim()) {
				update({ thatLabel: picked.title });
			}
			setSearchSlot(null);
		},
		[searchSlot, setSlot, spec.thisLabel, spec.thatLabel, update],
	);

	const canGenerate =
		!busy &&
		license.trim().length > 0 &&
		spec.thisLabel.trim().length > 0 &&
		spec.thatLabel.trim().length > 0;

	const handleGenerate = useCallback(async () => {
		setCreditSpent(false);
		setProgress({ phase: "authorizing", message: "Checking your license…" });

		try {
			const token = await mintAgentToken({ license });
			setCreditSpent(true);
			storeLicense({ license: license.trim() });
			setLicenseStatus((previous) =>
				previous ? { ...previous, aiVideos: token.aiVideos } : previous,
			);

			setProgress({ phase: "connecting", message: "Waking up the narrator…" });
			const narration = await generateNarration({
				token: token.token,
				model: token.model,
				spec,
				onProgress: ({ secondsGenerated }) =>
					setProgress({
						phase: "narrating",
						message: "Writing and voicing the script…",
						secondsGenerated,
					}),
			});

			setProgress({ phase: "composing", message: "Composing the frames…" });
			const scene = await buildScene({ spec, narration });

			setProgress({ phase: "ready", message: "" });
			setResult({ scene, narration, spec });
		} catch (error) {
			setProgress({
				phase: "error",
				message:
					error instanceof Error ? error.message : "Something went wrong.",
			});
		}
	}, [license, spec]);

	const searchQuery = useMemo(() => {
		if (searchSlot === "thisImage") return spec.thisLabel;
		if (searchSlot === "thatImage") return spec.thatLabel;
		return "";
	}, [searchSlot, spec.thisLabel, spec.thatLabel]);

	if (result) {
		return (
			<main className="mx-auto w-full max-w-6xl px-4 py-10 sm:px-6">
				<ResultView
					scene={result.scene}
					narration={result.narration}
					spec={result.spec}
					onStartOver={() => {
						setResult(null);
						setProgress({ phase: "idle", message: "" });
					}}
				/>
			</main>
		);
	}

	return (
		<main className="mx-auto w-full max-w-6xl px-4 pt-8 pb-28 sm:px-6 lg:pb-16">
			<div className="max-w-2xl">
				<span className="border-border/70 bg-card/50 text-muted-foreground mb-4 inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium">
					<Sparkles className="size-3" />
					AI comparison videos
				</span>
				<h1 className="text-3xl font-bold tracking-tight sm:text-4xl">
					Make a &ldquo;this vs that&rdquo; video
				</h1>
				<p className="text-muted-foreground mt-3 text-base">
					Pick two things. An AI narrator writes the script and speaks it, the
					character points at whichever side it's talking about, and you get an
					MP4 — rendered right here, no upload.
				</p>
				{/* A new arrival's first question is "what am I actually filling in?".
				    The numbered sections below answer it, but only after a scroll. */}
				<ol className="text-muted-foreground mt-5 flex flex-wrap gap-x-5 gap-y-2 text-xs">
					{[
						"Name the two things",
						"Add a logo for each",
						"Pick a voice and hit generate",
					].map((step, index) => (
						<li key={step} className="flex items-center gap-2">
							<span className="bg-primary/10 text-primary flex size-5 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold">
								{index + 1}
							</span>
							{step}
						</li>
					))}
				</ol>
			</div>

			{/* Explicit row/column placement so the preview can come first in the
			    DOM — on a phone it belongs above the form, not 2,700px below it —
			    while still sitting in the right-hand column on a desktop. */}
			<div className="mt-8 grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px] lg:gap-8">
				<aside className="lg:col-start-2 lg:row-start-1 lg:sticky lg:top-24 lg:max-h-[calc(100dvh-7rem)] lg:self-start lg:overflow-y-auto">
					<div className="flex items-center justify-between">
						<Label>Preview</Label>
						<div className="bg-muted flex rounded-md p-0.5">
							{(["this", "thinking", "that", "idle"] as PoseKind[]).map((pose) => (
								<button
									key={pose}
									type="button"
									onClick={() => setPreviewPose(pose)}
									className={cn(
										"cursor-pointer rounded-sm px-1.5 py-1 text-xs font-medium capitalize transition-colors",
										previewPose === pose
											? "bg-background shadow-sm"
											: "text-muted-foreground hover:text-foreground",
									)}
								>
									{pose === "thinking" ? "think" : pose}
								</button>
							))}
						</div>
					</div>

					{/* Capped by height on a phone: a 9:16 frame at full width was 636px
					    tall, so it was clipped top and bottom by the sticky site header
					    and the action bar. Width and height both auto with only maxima
					    set keeps the canvas's own aspect ratio at every format. */}
					<SceneCanvas
						scene={previewScene}
						timeMs={800}
						poseOverride={previewPose}
						className="mx-auto mt-2 h-auto max-h-[44vh] w-auto max-w-full lg:max-h-none lg:w-full"
					/>

					<div className="mt-4 hidden lg:block">
						<GenerateButton
							busy={busy}
							disabled={!canGenerate}
							onClick={() => void handleGenerate()}
						/>
						<GenerateHint
							hasLicense={license.trim().length > 0}
							hasLabels={
								spec.thisLabel.trim().length > 0 && spec.thatLabel.trim().length > 0
							}
						/>
					</div>

					{progress.phase !== "idle" && progress.phase !== "ready" ? (
						<StatusPanel
							progress={progress}
							creditSpent={creditSpent}
							className="mt-4 hidden lg:block"
						/>
					) : null}
				</aside>

				<div className="flex flex-col gap-6 lg:col-start-1 lg:row-start-1">
					<Section
						step={1}
						title="What are you comparing?"
						description="The narrator reads these two names out loud — and saying a name is what cues the pointing."
					>
						<Textarea
							value={spec.topic}
							rows={3}
							maxLength={600}
							placeholder="Instagram vs TikTok — which one actually grows a new audience faster?"
							// The shared Textarea borders itself with `border-input`, which is
							// the field FILL token (pure white in light mode) — invisible on
							// this card. Matching the Input's `border-border bg-input` gives
							// it an edge you can actually see.
							className="border-border bg-input"
							onChange={(event) => update({ topic: event.target.value })}
						/>

						<div className="mt-4 grid grid-cols-2 gap-3 sm:gap-4">
							{(
								[
									{ side: "Left", labelKey: "thisLabel", slot: "thisImage" },
									{ side: "Right", labelKey: "thatLabel", slot: "thatImage" },
								] as const
							).map(({ side, labelKey, slot }) => (
								<div key={side} className="flex flex-col gap-2">
									<Label htmlFor={`label-${slot}`}>
										{side} side{" "}
										<span className="text-muted-foreground font-normal">
											— name
										</span>
									</Label>
									<Input
										id={`label-${slot}`}
										value={spec[labelKey]}
										// Everyday brands, not developer tools — the placeholders
										// are the clearest signal of what this page is for, and
										// two infra names made it look developer-only.
										placeholder={side === "Left" ? "Instagram" : "TikTok"}
										onChange={(event) => update({ [labelKey]: event.target.value })}
									/>
									<ImageSlot
										image={spec[slot]}
										placeholder="Logo or image"
										hint="Optional"
										busy={busySlot === slot}
										error={slotError?.slot === slot ? slotError.message : null}
										// A fixed height rather than an aspect ratio: on a wide
										// column `aspect-4/3` made a picked logo tower over the
										// empty tile next to it.
										className="h-36"
										onFile={(file) => void handleFile({ slot, file })}
										onSearch={() => setSearchSlot(slot)}
										onClear={() => setSlot(slot, null)}
									/>
								</div>
							))}
						</div>
					</Section>

					<Section
						step={2}
						title="Pick a character"
						description="Whoever you pick does the pointing. Four poses: left, right, thinking, hands down."
					>
						<div className="flex flex-wrap gap-3">
							{CHARACTERS.map((character) => (
								<button
									key={character.id}
									type="button"
									onClick={() => update({ characterId: character.id })}
									className={cn(
										"flex w-24 cursor-pointer flex-col items-center gap-1 rounded-lg border p-2 transition-colors",
										spec.characterId === character.id
											? "border-primary bg-primary/5"
											: "border-border hover:border-primary/50",
									)}
								>
									{/* eslint-disable-next-line @next/next/no-img-element -- static SVG */}
									<img
										src={poseThumbnails[character.id] ?? character.poses.this}
										alt={character.name}
										className="h-20 w-full object-contain"
									/>
									<span className="text-xs font-medium">{character.name}</span>
								</button>
							))}
						</div>
					</Section>

					<Section
						step={3}
						title="Look"
						description="Colour, format, background and captions."
					>
						<div className="flex flex-col gap-4">
							<div>
								<Label>Theme colour</Label>
								<p className="text-muted-foreground mt-0.5 text-xs">
									Tints the character, the name chips and the spoken-word
									highlight together.
								</p>
								<div className="mt-2 flex flex-wrap items-center gap-2">
									{THEME_SWATCHES.map((color) => (
										<button
											key={color}
											type="button"
											aria-label={`Theme ${color}`}
											onClick={() => update({ themeColor: color })}
											style={{ backgroundColor: color }}
											className={cn(
												// The ring sits outside the swatch with a gap, so it stays
												// visible even on the swatch that matches `--primary`
												// — a same-colour border there showed nothing at all.
												"border-border size-8 cursor-pointer rounded-md border transition-transform",
												spec.themeColor.toLowerCase() === color
													? "ring-primary ring-offset-background scale-110 ring-2 ring-offset-2"
													: "",
											)}
										/>
									))}
									<input
										type="color"
										aria-label="Custom theme colour"
										value={spec.themeColor}
										onChange={(event) => update({ themeColor: event.target.value })}
										className="border-border size-8 cursor-pointer rounded-md border bg-transparent p-0.5"
									/>
								</div>
							</div>

							<div>
								<Label>Format</Label>
								<div className="mt-2 flex flex-wrap gap-2">
									{ASPECTS.map((aspect) => (
										<button
											key={aspect.id}
											type="button"
											onClick={() => update({ aspect: aspect.id })}
											className={cn(
												"cursor-pointer rounded-md border px-3 py-2 text-left transition-colors",
												spec.aspect === aspect.id
													? "border-primary bg-primary/5"
													: "border-border hover:border-primary/50",
											)}
										>
											<span className="block text-sm font-medium">{aspect.label}</span>
											<span className="text-muted-foreground block text-xs">
												{aspect.hint}
											</span>
										</button>
									))}
								</div>
							</div>

							<div>
								<Label>Background</Label>
								<div className="mt-2 flex flex-wrap items-center gap-2">
									{BACKGROUND_SWATCHES.map((color) => (
										<button
											key={color}
											type="button"
											aria-label={`Background ${color}`}
											onClick={() => {
												setSlot("background", null);
												update({ backgroundColor: color });
											}}
											style={{ backgroundColor: color }}
											className={cn(
												"border-border size-8 cursor-pointer rounded-md border transition-transform",
												!spec.background && spec.backgroundColor === color
													? "ring-primary ring-offset-background scale-110 ring-2 ring-offset-2"
													: "",
											)}
										/>
									))}
									<input
										type="color"
										aria-label="Custom background colour"
										value={spec.backgroundColor}
										onChange={(event) => {
											setSlot("background", null);
											update({ backgroundColor: event.target.value });
										}}
										className="border-border size-8 cursor-pointer rounded-md border bg-transparent p-0.5"
									/>
								</div>
								{/* On its own row: inline with the 32px swatches the tile was too
								    short and too narrow for its own buttons, which spilled out
								    of the dashed box. */}
								<ImageSlot
									image={spec.background}
									placeholder="Or use an image"
									busy={busySlot === "background"}
									error={
										slotError?.slot === "background" ? slotError.message : null
									}
									className="mt-3 h-32 w-full sm:w-64"
									onFile={(file) => void handleFile({ slot: "background", file })}
									onSearch={() => setSearchSlot("background")}
									onClear={() => setSlot("background", null)}
								/>
							</div>

							<label className="border-border hover:bg-accent/40 flex cursor-pointer items-center justify-between gap-4 rounded-lg border p-3 transition-colors">
								<span>
									<span className="block text-sm font-medium">
										Show captions{" "}
										<span
											className={cn(
												"ml-1 rounded px-1.5 py-0.5 text-[10px] font-semibold tracking-wide uppercase",
												spec.showCaptions
													? "bg-primary/15 text-primary"
													: "bg-muted text-muted-foreground",
											)}
										>
											{spec.showCaptions ? "On" : "Off"}
										</span>
									</span>
									<span className="text-muted-foreground block text-xs">
										The spoken word is highlighted as it's said.
									</span>
								</span>
								<Switch
									checked={spec.showCaptions}
									onCheckedChange={(checked) => update({ showCaptions: checked })}
									// The shared switch uses `bg-input` when off, which is nearly
									// invisible on this card — it reads as the control vanishing.
									className="data-[state=unchecked]:bg-foreground/25"
								/>
							</label>
						</div>
					</Section>

					<Section
						step={4}
						title="Voice & length"
						description="The narrator speaks the language you choose here."
					>
						<div className="grid gap-3 sm:grid-cols-3">
							<div>
								<Label>Language</Label>
								<Select
									value={spec.languageLabel}
									onValueChange={(value) => update({ languageLabel: value })}
								>
									<SelectTrigger className="mt-1.5 w-full">
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										{LANGUAGES.map((language) => (
											<SelectItem key={language.code} value={language.label}>
												{language.label}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>

							<div>
								<Label>Voice</Label>
								<Select
									value={spec.voiceName}
									onValueChange={(value) => update({ voiceName: value })}
								>
									<SelectTrigger className="mt-1.5 w-full">
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										{VOICES.map((voice) => (
											<SelectItem key={voice.name} value={voice.name}>
												{voice.name} — {voice.description}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>

							<div>
								<Label>Length</Label>
								<Select
									value={String(spec.targetSeconds)}
									onValueChange={(value) =>
										update({ targetSeconds: Number(value) })
									}
								>
									<SelectTrigger className="mt-1.5 w-full">
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										{DURATIONS.map((length) => (
											<SelectItem key={length.seconds} value={String(length.seconds)}>
												{length.label}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>
						</div>
					</Section>

					<Section
						title="Watermark"
						optional
						description="Text or an image, in any corner."
					>
						<div className="grid gap-3 sm:grid-cols-[1fr_auto]">
							<div>
								<Label htmlFor="watermark-text">Text</Label>
								<Input
									id="watermark-text"
									value={spec.watermarkText}
									placeholder="@yourhandle"
									maxLength={40}
									className="mt-1.5"
									onChange={(event) => update({ watermarkText: event.target.value })}
								/>
								<div className="mt-3">
									<Label>Corner</Label>
									<Select
										value={spec.watermarkCorner}
										onValueChange={(value) =>
											update({ watermarkCorner: value as WatermarkCorner })
										}
									>
										<SelectTrigger className="mt-1.5 w-full sm:w-48">
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											{WATERMARK_CORNERS.map((corner) => (
												<SelectItem key={corner.value} value={corner.value}>
													{corner.label}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								</div>
							</div>
							<ImageSlot
								image={spec.watermarkImage}
								placeholder="Logo instead"
								busy={busySlot === "watermarkImage"}
								error={
									slotError?.slot === "watermarkImage" ? slotError.message : null
								}
								className="h-32 w-full sm:w-40"
								onFile={(file) => void handleFile({ slot: "watermarkImage", file })}
								onClear={() => setSlot("watermarkImage", null)}
							/>
						</div>
						{spec.watermarkImage ? (
							<p className="text-muted-foreground mt-2 text-xs">
								The image wins over the text.
							</p>
						) : null}
					</Section>

					{/* Last, not first: asking a new arrival for a key before they know
					    what the page makes is a wall, and the Generate button links
					    back down here when one is still missing. */}
					<Section
						id="license"
						title="License key"
						description="The same key that unlocks the extension. Saved in this browser, and one credit is spent per video."
					>
						<div className="flex flex-col gap-2 sm:flex-row">
							<Input
								type="text"
								value={license}
								placeholder="Paste your license key"
								autoComplete="off"
								spellCheck={false}
								onChange={(event) => {
									setLicense(event.target.value);
									setLicenseStatus(null);
									setLicenseError(null);
								}}
								onBlur={() => void verifyLicense({ key: license })}
							/>
							<Button
								type="button"
								variant="outline"
								disabled={!license.trim() || checkingLicense}
								onClick={() => void verifyLicense({ key: license })}
							>
								{checkingLicense ? <Loader2 className="size-4 animate-spin" /> : null}
								Check
							</Button>
						</div>
						{licenseError ? (
							<p className="text-destructive mt-2 text-xs">{licenseError}</p>
						) : null}
						{/* A valid key with nothing left still passes `canGenerate`,
						    so the run would only fail at the mint. Say it here
						    instead, next to the key, with the way out. */}
						{licenseStatus?.valid && licenseStatus.aiVideos <= 0 ? (
							<p className="mt-2 text-xs">
								Key accepted, but you have no AI videos left.{" "}
								<Link href="/#pricing" className="underline underline-offset-2">
									Get more
								</Link>
								.
							</p>
						) : licenseStatus?.valid ? (
							<p className="text-muted-foreground mt-2 text-xs">
								Key accepted — {licenseStatus.aiVideos} AI video
								{licenseStatus.aiVideos === 1 ? "" : "s"} left.
							</p>
						) : null}
						{!license.trim() ? (
							<p className="text-muted-foreground mt-3 text-xs">
								Don't have a key?{" "}
								<Link href="/#pricing" className="underline underline-offset-2">
									Get the Lifetime plan
								</Link>{" "}
								— it includes 25 AI videos, one credit per video.
							</p>
						) : null}
					</Section>
				</div>

			</div>

			{/* Mobile action bar. The status panel lives here too, not only in the
			    desktop column — an error rendered solely in that column was ~2,700px
			    away from whatever the user was looking at on a phone. */}
			<div className="bg-background/95 border-border fixed inset-x-0 bottom-0 z-40 border-t p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] backdrop-blur lg:hidden">
				{progress.phase !== "idle" && progress.phase !== "ready" ? (
					<StatusPanel
						progress={progress}
						creditSpent={creditSpent}
						className="mb-2"
					/>
				) : null}
				<GenerateButton
					busy={busy}
					disabled={!canGenerate}
					onClick={() => void handleGenerate()}
				/>
				<GenerateHint
					hasLicense={license.trim().length > 0}
					hasLabels={
						spec.thisLabel.trim().length > 0 && spec.thatLabel.trim().length > 0
					}
				/>
			</div>

			<ImageSearchSheet
				open={searchSlot !== null}
				onOpenChange={(open) => {
					if (!open) setSearchSlot(null);
				}}
				title={searchSlot ? SLOT_TITLES[searchSlot] : ""}
				initialQuery={searchQuery}
				onPick={(picked) => handleSearchPick({ result: picked })}
			/>
		</main>
	);
}

function Section({
	id,
	step,
	title,
	optional,
	description,
	children,
}: {
	id?: string;
	/** Numbered so the required path reads as a sequence, not six equal cards. */
	step?: number;
	title: string;
	optional?: boolean;
	description?: string;
	children: React.ReactNode;
}) {
	return (
		<section
			id={id}
			className="border-border bg-card/40 scroll-mt-24 rounded-xl border p-4 sm:p-5"
		>
			<div className="flex items-center gap-2">
				{step ? (
					<span className="bg-primary/10 text-primary flex size-5 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold">
						{step}
					</span>
				) : null}
				<h2 className="text-sm font-semibold">{title}</h2>
				{optional ? (
					<span className="bg-muted text-muted-foreground rounded px-1.5 py-0.5 text-[10px] font-semibold tracking-wide uppercase">
						Optional
					</span>
				) : null}
			</div>
			{description ? (
				<p className="text-muted-foreground mt-0.5 mb-3 text-xs">{description}</p>
			) : (
				<div className="mb-3" />
			)}
			{children}
		</section>
	);
}

/** Says which field is still blocking Generate, and links straight to it. */
function GenerateHint({
	hasLicense,
	hasLabels,
}: {
	hasLicense: boolean;
	hasLabels: boolean;
}) {
	if (hasLabels && hasLicense) return null;

	return (
		<p className="text-muted-foreground mt-2 text-center text-xs lg:text-left">
			{!hasLabels ? (
				"Name both sides to generate."
			) : (
				<>
					Add your{" "}
					<a href="#license" className="underline underline-offset-2">
						license key
					</a>{" "}
					to generate.
				</>
			)}
		</p>
	);
}

function GenerateButton({
	busy,
	disabled,
	onClick,
}: {
	busy: boolean;
	disabled: boolean;
	onClick: () => void;
}) {
	return (
		<Button
			type="button"
			size="lg"
			disabled={disabled}
			onClick={onClick}
			className="h-11 w-full bg-[#4d2ef5] text-white hover:bg-[#3d1fe0]"
		>
			{busy ? (
				<Loader2 className="size-4 animate-spin" />
			) : (
				<Sparkles className="size-4" />
			)}
			{busy ? "Generating…" : "Generate video"}
			{!busy ? <ArrowRight className="size-4" /> : null}
		</Button>
	);
}

function StatusPanel({
	progress,
	creditSpent,
	className,
}: {
	progress: GenerationProgress;
	creditSpent: boolean;
	className?: string;
}) {
	if (progress.phase === "error") {
		return (
			<div
				className={cn(
					"border-destructive/40 bg-destructive/5 rounded-lg border p-3",
					className,
				)}
			>
				<p className="text-destructive flex items-start gap-2 text-sm">
					<AlertCircle className="mt-0.5 size-4 shrink-0" />
					{progress.message}
				</p>
				{creditSpent ? (
					<p className="text-muted-foreground mt-2 text-xs">
						This attempt used one AI video credit.
					</p>
				) : null}
			</div>
		);
	}

	return (
		<div className={cn("border-border bg-card/60 rounded-lg border p-3", className)}>
			<p className="flex items-center gap-2 text-sm">
				<Loader2 className="size-4 animate-spin" />
				{progress.message}
			</p>
			{progress.secondsGenerated ? (
				<p className="text-muted-foreground mt-1 text-xs">
					{progress.secondsGenerated.toFixed(1)}s of narration so far — don't
					close this tab.
				</p>
			) : null}
		</div>
	);
}
